import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { Loader } from "rsuite";
import { TbHandClick } from "react-icons/tb";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import { axInst } from "../../../Components/Login/Service";

/* ================= API CALLS ================= */

const regionApi = (body) =>
  axInst.post("/realization/nfa_region_wise", body).then((r) => r.data);

const spmApi = (body) =>
  axInst.post("/realization/nfa_spm_wise", body).then((r) => r.data);

const tsmApi = (body) =>
  axInst.post("/realization/nfa_tsm_wise", body).then((r) => r.data);

const dealerApi = (body) =>
  axInst.post("/realization/nfa_dealer_wise", body).then((r) => r.data);

/* ================= COMPONENT ================= */

export const NfaApproved = () => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersRealization,
  );

  const { data, isLoading } = useQuery({
    queryKey: ["nfaRegion", submitted],
    queryFn: () => regionApi(filterdata),
    refetchInterval: 300000,
    cacheTime: 0,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });

  const [expanded, setExpanded] = useState({});

  /* ================= DRILLDOWN ================= */

  const toggleRegion = async (region) => {
    if (expanded[region]) {
      setExpanded((p) => ({ ...p, [region]: null }));
      return;
    }

    const spm = await spmApi({ ...filterdata, region: [region] });

    setExpanded((p) => ({
      ...p,
      [region]: { spm, expanded: {} },
    }));
  };

  const toggleSpm = async (region, spm) => {
    const ref = expanded[region].expanded;

    if (ref[spm]) {
      delete ref[spm];
      setExpanded({ ...expanded });
      return;
    }

    const tsm = await tsmApi({
      ...filterdata,
      region: [region],
      Spm_Name: [spm],
    });

    ref[spm] = { tsm, expanded: {} };
    setExpanded({ ...expanded });
  };

  const toggleTsm = async (region, spm, tsm) => {
    const ref = expanded[region].expanded[spm].expanded;

    if (ref[tsm]) {
      delete ref[tsm];
      setExpanded({ ...expanded });
      return;
    }

    const dealer = await dealerApi({
      ...filterdata,
      region: [region],
      Spm_Name: [spm],
      Tsm_Name: [tsm],
    });

    ref[tsm] = dealer;
    setExpanded({ ...expanded });
  };

  /* ================= TOTAL CALC ================= */

  const values = Object.values(data || {});
  const total = values.reduce(
    (acc, curr) => ({
      TSM_COUNT: acc.TSM_COUNT + Number(curr.TSM_COUNT || 0),
      SPM_COUNT: acc.SPM_COUNT + Number(curr.SPM_COUNT || 0),
      RSM_COUNT: acc.RSM_COUNT + Number(curr.RSM_COUNT || 0),
      NPM_COUNT: acc.NPM_COUNT + Number(curr.NPM_COUNT || 0),
      GRAND_TOTAL: acc.GRAND_TOTAL + Number(curr.GRAND_TOTAL || 0),
      NFA_APPROVED: acc.NFA_APPROVED + Number(curr.NFA_APPROVED || 0),
    }),
    {
      TSM_COUNT: 0,
      SPM_COUNT: 0,
      RSM_COUNT: 0,
      NPM_COUNT: 0,
      GRAND_TOTAL: 0,
      NFA_APPROVED: 0,
    },
  );

  total.NFA_APPROVED = values.length
    ? (total.NFA_APPROVED / values.length).toFixed(2) + "%"
    : "0.00%";

  const REGION_ORDER = ["North", "East", "West", "South"];
  /* ================= ROW RENDER ================= */

  const renderRow = (name, d, indent, arrow, onClick, bg) => (
    <tr onClick={onClick} className={`${bg} cursor-pointer `}>
      <td
        className={` pl-3 py-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative flex items-center gap-2 whitespace-nowrap`}
      >
        {name}
        {arrow}
      </td>
      <td className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative">
        {d.TSM_COUNT}
      </td>
      <td className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative">
        {d.SPM_COUNT}
      </td>
      <td className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative">
        {d.RSM_COUNT}
      </td>
      <td className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative">
        {d.NPM_COUNT}
      </td>
      <td className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative">
        {d.GRAND_TOTAL}
      </td>
      <td className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative">
        {d.NFA_APPROVED}
      </td>
    </tr>
  );

  /* ================= UI ================= */

  return (
    <div className="p-2">
      <h2 className="text-sm font-semibold text-[#545454] mb-2">
        NFA Approved – Region → SPM → TSM → Dealer Name
      </h2>

      <div className="h-[212px] overflow-y-auto md:overflow-x-hidden overflow-x-auto">
        {isLoading && (
          <div className="grid place-items-center h-full">
            <Loader />
          </div>
        )}

        {!isLoading && (
          <>
            <WaterMark />

            <table className="w-full text-xs border-collapse text-center">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                <tr>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Name
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    TSM
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    SPM
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    RSM
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    NPM
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Total
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    NFA Approved %
                  </th>
                </tr>
              </thead>

              <tbody className="text-center">
                {REGION_ORDER.filter((region) => data[region]).map((region) => {
                  const r = data[region];
                  return (
                    <React.Fragment key={region}>
                      {renderRow(
                        region,
                        r,
                        3,
                        expanded[region] ? (
                          <IoMdArrowDropup />
                        ) : (
                          <IoMdArrowDropdown />
                        ),
                        () => toggleRegion(region),
                        "even:bg-gray-200 odd:bg-white ",
                      )}

                      {expanded[region] &&
                        Object.entries(expanded[region].spm).map(([spm, s]) => (
                          <React.Fragment key={spm}>
                            {renderRow(
                              spm,
                              s,
                              6,
                              expanded[region].expanded[spm] ? (
                                <IoMdArrowDropup />
                              ) : (
                                <IoMdArrowDropdown />
                              ),
                              () => toggleSpm(region, spm),
                              "bg-[#2a5775] text-white",
                            )}

                            {expanded[region].expanded[spm] &&
                              Object.entries(
                                expanded[region].expanded[spm].tsm,
                              ).map(([tsm, t]) => (
                                <React.Fragment key={tsm}>
                                  {renderRow(
                                    tsm,
                                    t,
                                    9,
                                    expanded[region].expanded[spm].expanded[
                                      tsm
                                    ] ? (
                                      <IoMdArrowDropup />
                                    ) : (
                                      <IoMdArrowDropdown />
                                    ),
                                    () => toggleTsm(region, spm, tsm),
                                    "bg-slate-100",
                                  )}

                                  {expanded[region].expanded[spm].expanded[
                                    tsm
                                  ] &&
                                    Object.entries(
                                      expanded[region].expanded[spm].expanded[
                                        tsm
                                      ],
                                    ).map(([dealer, d]) =>
                                      renderRow(
                                        dealer,
                                        d,
                                        12,
                                        null,
                                        null,
                                        "bg-slate-200",
                                      ),
                                    )}
                                </React.Fragment>
                              ))}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  );
                })}
                {/* TOTAL ROW */}
                {data && (
                  <tr className="bg-[#2A5775] text-white font-semibold">
                    <td className="pl-3 py-[2px] border border-gray-300 text-left">
                      TOTAL
                    </td>
                    <td className="border border-gray-300">
                      {total.TSM_COUNT}
                    </td>
                    <td className="border border-gray-300">
                      {total.SPM_COUNT}
                    </td>
                    <td className="border border-gray-300">
                      {total.RSM_COUNT}
                    </td>
                    <td className="border border-gray-300">
                      {total.NPM_COUNT}
                    </td>
                    <td className="border border-gray-300">
                      {total.GRAND_TOTAL}
                    </td>
                    <td className="border border-gray-300">
                      {total.NFA_APPROVED}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <p className="text-[11px] mt-2 flex items-center gap-1 text-slate-600">
              <TbHandClick size={14} />
              Click on Region to view Details
            </p>
          </>
        )}
      </div>
    </div>
  );
};
